import React, { useState, useEffect } from "react";
import ArrowDefault from "../../../../assets/icons/ArrowDefault.svg";
import ArrowSparkle from "../../../../assets/icons/ArrowSparkle.svg";
import "./arrowButton.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ArrowButton = ({ projectId, user, defaultFired = false, initialFireCount = 0 }) => {
  const [fired, setFired] = useState(defaultFired);
  const [fireCount, setFireCount] = useState(initialFireCount);
  const [firedBy, setFiredBy] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExploding, setIsExploding] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  let tooltipTimer = null;

  // 🔹 Fetch status + list
  useEffect(() => {
    const fetchFireStatus = async () => {
      if (!user?.email) return;
      try {
        const res = await fetch(`${BACKEND_URL}/engagements/fire/${projectId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json();
        if (res.ok) {
          setFired(data.fired || false);
          if (typeof data.fireCount === "number") {
            setFireCount(data.fireCount);
          }
        }
      } catch (err) {
        console.error("⬆️ Error checking fire status:", err);
      }
    };

    const fetchFiredBy = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/engagements/fire/${projectId}/fired-by`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const data = await res.json();
        if (res.ok) {
          const names = data.users.map((u) => u.firstName).filter(Boolean);
          setFiredBy(names);
        }
      } catch (err) {
        console.error("⬆️ Error fetching firedBy list:", err);
      }
    };

    fetchFireStatus();
    fetchFiredBy();
  }, [projectId, user?.email]);

  // 🔹 Toggle fire (vote)
  const toggleFire = async (e) => {
    e.stopPropagation();
    setLoading(true);

    const payload = { value: !fired, firstName: user?.firstName };

    try {
      const res = await fetch(`${BACKEND_URL}/engagements/fire/${projectId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok) {
        setFired(data.fired);
        setFireCount(data.fireCount);

        if (data.fired) {
          setIsExploding(true);
          setTimeout(() => setIsExploding(false), 400);
        }

        const updatedNames = [...firedBy];
        if (data.fired && !updatedNames.includes(user?.firstName)) {
          updatedNames.unshift(user?.firstName);
        } else if (!data.fired) {
          const index = updatedNames.indexOf(user?.firstName);
          if (index !== -1) updatedNames.splice(index, 1);
        }
        setFiredBy(updatedNames);
      }
    } catch (err) {
      console.error("⬆️ Failed to toggle fire:", err);
    }

    setLoading(false);
  };

  const handleMouseEnter = () => {
    if (loading || isExploding) return;
    tooltipTimer = setTimeout(() => setShowTooltip(true), 500);
  };

  const handleMouseLeave = () => {
    clearTimeout(tooltipTimer);
    setShowTooltip(false);
  };

  return (
    <div className="relative flex flex-col items-center">
      <button
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={toggleFire}
        disabled={loading}
        className={`flex items-center space-x-1 transition-transform relative ${
          isExploding ? "animate-arrow" : ""
        }`}
      >
        <img
          src={fired ? ArrowSparkle : ArrowDefault}
          alt="vote"
          className="w-5 h-5"
        />
        <span className="text-xs">{fireCount}</span>

        {/* ✨ Sparkle overlay */}
        {isExploding && <span className="sparkle animate-sparkle" />}
      </button>

      {showTooltip && firedBy.length > 0 && (
        <div className="absolute z-10 bottom-full mb-2 right-full mr-2 flex flex-col items-end bg-white shadow-lg border border-gray-200 rounded-md p-2 text-[11px] text-gray-800 whitespace-nowrap">
          <div className="font-semibold mb-1">⬆️ by:</div>
          <div>
            {firedBy.slice(0, 3).join(", ")}
            {firedBy.length > 3 && ` +${firedBy.length - 3}`}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArrowButton;
